/**
 * AuthShell
 *
 * Shared visual chrome for every unauthenticated screen (Login, Forgot
 * Password, Select Role, Select UDA).
 *
 * The composition is one full-bleed government plate with the authentication
 * panel floating on it. The photographed planned city, its navy wash and
 * `PlanningBackdrop`'s master-plan line work cover the entire viewport edge to
 * edge; over that sit the national tricolour rule, the state emblem lockup, the
 * department's statement and the card itself. Nothing is boxed into a half of
 * the screen — the imagery is the page, and the card reads as a panel resting
 * on it.
 *
 * LAYOUT CONTRACT. From `lg` the shell is exactly one viewport tall and the
 * page itself never scrolls: the plate always ends at the window edge. The two
 * columns each own that full height — the branding column stacks lockup,
 * statement and credit rail inside it, which is what leaves the card column
 * free to centre the card against the whole window rather than in what is left
 * between a masthead and a footer. Only the card column can scroll, and only on
 * a window too short to hold the card, which beats clipping the sign-in button.
 *
 * Below `lg` everything reverts to a single stacked column: lockup, card,
 * credit rail, with the page scrolling normally. Phones therefore get a
 * login-first layout that still carries the full branding, not a desktop layout
 * with half of it hidden.
 *
 * NOTE ON ELEMENT SELECTORS: `src/index.css` repaints bare `header` and `main`
 * elements in dark mode with `!important` — grey chrome for one, an opaque body
 * wash for the other. Both would destroy this plate, so the lockup and the card
 * column are divs carrying `role="banner"` / `role="main"`: same landmarks, no
 * selector match. Do not convert them back to the elements.
 *
 * NOTE ON WHITES: `src/index.css` carries a blanket
 * `.dark .bg-white { background-color: var(--portal-bg-card) !important }`.
 * The national tricolour band and the emblem disc are fixed marks — they are
 * white in every theme — and the plate they sit on is navy regardless of theme,
 * so they are written as `bg-[#ffffff]`, which that selector does not match.
 * Do not "tidy" those back to `bg-white`.
 *
 * Presentation only — this component holds no auth logic. Each page passes its
 * header, form and footer links as `children`.
 */

import React from 'react';
import './auth-shell.css';

/** Source plate for the hero: warm sky, master-plan sheet and river skyline. */
const HERO_IMAGE = '/images/hero/login-bg.png';

/* What this directorate actually does, in its own words. Kept to three.
   Hidden for now along with the pillar list below.
const PILLARS = [
  { icon: 'ri-map-2-line', label: 'Master plans & layout sanctions' },
  { icon: 'ri-building-4-line', label: 'Building permissions & regulation' },
  { icon: 'ri-road-map-line', label: 'GIS-backed land & parcel records' },
];
*/

/** A solid saffron rule across the top of the auth screens. */
const TricolourRule: React.FC = () => (
  <div className="relative z-20 h-1 w-full shrink-0 bg-[#FF9933]" aria-hidden="true" />
);

/** The state emblem, shown on its own with no disc behind it. `disc` sizes the box, `mark` the emblem inside it. */
const Emblem: React.FC<{ disc: string; mark: string }> = ({ disc, mark }) => (
  <div
    className={`flex ${disc} shrink-0 items-center justify-center`}
  >
    <img
      src="/images/header/ap-logo-large.png"
      alt="Emblem of the Government of Andhra Pradesh"
      className={`${mark} object-contain`}
    />
  </div>
);

/**
 * The full-bleed plate: the login artwork, shown in its own colours with no
 * scrim, line work or motion over it. The artwork is light, so the type over
 * it is navy.
 *
 * It is pinned to the shell root, which is `min-h-screen` and grows with its
 * content, so the plate covers the whole page at every viewport — including the
 * taller Forgot Password flows on a phone, where a viewport-only backdrop would
 * end mid-scroll and leave the rest of the page bare.
 */
const HeroPlate: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
    <img
      src={HERO_IMAGE}
      alt=""
      aria-hidden="true"
      className="h-full w-full object-cover object-[50%_58%]"
      loading="eager"
      decoding="async"
    />
  </div>
);

/**
 * The credit rail that closes the screen.
 *
 * Rendered in two places — inside the branding column from `lg`, and as a
 * full-width row below it — because its position in the reading order differs
 * between the layouts. Stacked, it has to come after the card; in two columns
 * it belongs at the foot of the branding column, so the card column can own the
 * full height. One component, so the copy cannot drift between them.
 */
const CreditRail: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={`flex flex-col items-center gap-1.5 border-t border-[#0a1e3f]/10 pt-3 text-center sm:flex-row sm:justify-start sm:gap-6 sm:text-left ${className}`}
  >
    <p className="text-[11px] text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.45)] sm:text-xs">
      Developed by{' '}
      <span className="font-semibold text-white">
        Dreamstep Software Innovations Pvt Ltd.
      </span>
    </p>
  </div>
);

interface AuthShellProps {
  /** Label shown in the slim band at the top of the card. */
  bandLabel?: string;
  /** The card body — header, form, footer links, etc. */
  children: React.ReactNode;
}

const AuthShell: React.FC<AuthShellProps> = ({ bandLabel, children }) => {
  return (
    <div className="dtcp-auth relative isolate flex min-h-screen flex-col bg-[#fbefdc] text-[#0a1e3f] lg:h-screen lg:overflow-hidden">
      <HeroPlate />
      <TricolourRule />

      <div className="relative z-10 flex flex-1 flex-col lg:min-h-0 lg:flex-row lg:items-stretch">
        {/* ══════════ Branding column ══════════ */}
        <div className="flex flex-col px-5 sm:px-8 lg:min-h-0 lg:flex-1 lg:px-12 lg:pb-4 lg:pr-8 xl:px-16 xl:pr-10">
          <div
            role="banner"
            className="dtcp-rise dtcp-d1 flex items-center gap-3.5 pt-4 sm:gap-4 sm:pt-5 lg:pt-6"
          >
            <Emblem disc="h-16 w-16 sm:h-20 sm:w-20" mark="h-16 w-16 sm:h-20 sm:w-20" />
            <div className="min-w-0">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#b45309] sm:text-[13px] sm:tracking-[0.22em]">
                Government of Andhra Pradesh
              </p>
              {/* Below lg the statement is hidden, so the lockup carries the
                  directorate's name itself rather than the parent department's. */}
              <p className="mt-0.5 font-['Poppins',sans-serif] text-[15px] font-semibold text-[#9a3f07] leading-tight sm:mt-1 sm:text-lg lg:hidden">
                Directorate of Town &amp; Country Planning
              </p>
              <p className="mt-1 hidden text-sm font-medium leading-snug text-[#0a1e3f] lg:block">
                Municipal Administration &amp; Urban Development Department
              </p>
            </div>
          </div>

          {/* ── Department statement (lg and up), centred in the leftover height ── */}
          <div className="hidden lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:justify-center lg:pt-6 lg:pb-20">
            <div className="dtcp-rise dtcp-d2 flex items-center gap-3">
              <span className="h-px w-8 bg-[#b7791f]" aria-hidden="true" />
              <span className="text-[12.5px] font-semibold uppercase tracking-[0.3em] text-[#b7791f]">
                DTCP ERP
              </span>
            </div>

            <h1 className="dtcp-rise dtcp-d3 mt-5 font-['Poppins',sans-serif] text-[2.4rem] font-semibold leading-[1.2] tracking-[-0.01em] text-[#9a3f07] [text-shadow:0_1px_1px_rgba(255,255,255,0.6),0_2px_8px_rgba(89,40,4,0.12)] xl:text-[2.9rem]">
              Directorate of Town
              <br />
              &amp; Country Planning
            </h1>

            <p className="dtcp-rise dtcp-d4 mt-6 max-w-lg text-[16.5px] font-medium leading-relaxed text-[#0a1e3f]/85 xl:text-[17.5px]">
              Digital governance for planned and sustainable urban development
              across Andhra Pradesh.
            </p>

            {/* Pillars hidden for now.
            <ul className="dtcp-rise dtcp-d5 mt-8 max-w-md space-y-3 [@media(max-height:700px)]:hidden">
              {PILLARS.map((pillar) => (
                <li
                  key={pillar.label}
                  className="flex items-center gap-3 rounded-lg border border-[#0a1e3f]/10 bg-[#ffffff]/85 px-4 py-2.5 shadow-sm"
                >
                  <i className={`${pillar.icon} text-xl text-[#b7791f]`} aria-hidden="true" />
                  <span className="text-[15px] font-medium text-[#0a1e3f]">{pillar.label}</span>
                </li>
              ))}
            </ul>
            */}
          </div>

          <CreditRail className="dtcp-rise dtcp-d6 hidden lg:flex" />
        </div>

        {/* ══════════ Authentication column ══════════
            From lg the column is laid over the whole content area, so the
            card's `m-auto` centres it on the screen. */}
        <div
          role="main"
          className="flex px-5 py-5 sm:px-8 lg:absolute lg:inset-0 lg:overflow-y-auto lg:pl-4 lg:pr-32 lg:pt-4 lg:pb-40"
        >
          <div className="m-auto w-full max-w-[30rem]">
            {/* A tablet has room for the statement above the card; a phone does
                not, and the plate itself carries the atmosphere there. */}
            <div className="dtcp-rise dtcp-d2 mb-6 hidden text-center sm:block lg:hidden">
              <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#b7791f]">
                DTCP ERP
              </p>
              <p className="mx-auto mt-2 max-w-md text-[15px] leading-relaxed text-[#0a1e3f]/85">
                Digital governance for planned and sustainable urban development
                across Andhra Pradesh.
              </p>
            </div>

            <div className="dtcp-card-in overflow-hidden rounded-2xl bg-white shadow-[0_30px_70px_-20px_rgba(2,10,26,0.75)] ring-1 ring-white/15 dark:ring-white/10">
              {/* Card band — gold hairline over deep blue, matching the plate */}
              {bandLabel && (
                <div>
                  <div
                    className="h-[3px] w-full bg-gradient-to-r from-[#c2641a] via-[#f0a24a] to-[#c2641a]"
                    aria-hidden="true"
                  />
                  <div className="flex items-center gap-2.5 bg-gradient-to-r from-[#e08a34] to-[#cf7420] px-6 py-2 sm:px-8">
                    <i className="ri-government-line text-white" aria-hidden="true" />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white">
                      {bandLabel}
                    </span>
                  </div>
                </div>
              )}

              <div className="px-6 py-6 sm:px-8 text-gray-900 dark:text-white">{children}</div>
            </div>
          </div>
        </div>
      </div>

      <CreditRail className="dtcp-rise dtcp-d6 relative z-10 mx-5 mb-3 sm:mx-8 lg:hidden" />
    </div>
  );
};

export default AuthShell;
